import { FC, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Button, FilterInput } from '../../../components';
import { ButtonSize, ButtonColor } from '../../../enums';
import strings from '../../../language/Swedish';
import { AdsMarketSharesType } from '../../../types/apiTypes';
import { formatNumber } from '../../../helpers';

const FilterWrapper = styled.div`
  ${tw`relative flex items-center mb-6 w-full max-w-4xl text-xs`};
`;

const UnorderedList = styled.ul`
  ${tw`mb-4 space-y-1`};
`;

const StyledListItem = styled.li`
  ${tw`relative flex items-center p-2 [&>*]:px-2`};

  & > * {
    ${tw`px-2`};
  }
`;

const StyledI = styled.i<{ $isTop10: boolean; $color: string }>`
  ${tw`absolute inset-0 rounded-lg opacity-25 transition-opacity border cursor-pointer`};

  background-color: ${({ $isTop10, $color }) =>
    $isTop10 ? `rgba(${$color}, 0.2)` : 'transparent'};
  border-color: ${({ $isTop10, $color }) =>
    $isTop10 ? `rgba(${$color}, 0.6)` : 'transparent'};

  &:hover {
    ${tw`opacity-100 border`};
  }
`;

const Item = styled.div<{ $isTop10: boolean; $color: string }>`
  ${tw`h-4 w-4 rounded-full`};

  background-color: ${({ $isTop10, $color }) =>
    $isTop10 ? `rgba(${$color}, 0.6)` : 'transparent'};
  border-color: ${({ $isTop10, $color }) =>
    $isTop10 ? `rgba(${$color}, 1)` : 'transparent'};
`;

const Label = styled.div`
  ${tw`flex-1`};
`;

const StyledButton = styled(Button)`
  ${tw`mb-6`};
`;

const colorsTop10 = [
  '53, 123, 28',
  '0, 116, 159',
  '115, 199, 115',
  '199, 117, 76',
  '23, 189, 184',
  '254, 192, 202',
  '128, 128, 0',
  '128, 128, 128',
  '210, 105, 30',
  '249, 128, 114',
];

interface Props {
  listData: AdsMarketSharesType[];
  noFilter?: boolean;
}

const BrokersList: FC<Props> = ({ listData, noFilter }) => {
  const [showMore, setShowMore] = useState<boolean>(noFilter || false);
  const [filterValue, setFilterValue] = useState('');

  const sortedList = listData.sort((a, b) => b.market_share - a.market_share);

  const itemsToShow = showMore ? sortedList : sortedList.slice(0, 10);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    if (e.target.value === '') setShowMore(false);
    else setShowMore(true);
  };

  const clearFilter = () => {
    setFilterValue('');
    setShowMore(false);
  };

  return (
    <>
      {!noFilter && (
        <FilterWrapper>
          <FilterInput
            filterValue={filterValue}
            handleChange={handleFilterChange}
            clearFilter={clearFilter}
          />
        </FilterWrapper>
      )}
      <UnorderedList>
        {itemsToShow.map(({ firm, market_share }, index) => {
          if (
            (filterValue &&
              firm.toLowerCase().includes(filterValue.toLowerCase())) ||
            filterValue === ''
          ) {
            return (
              <StyledListItem key={index}>
                <StyledI
                  $isTop10={index < 10}
                  $color={index < 10 ? colorsTop10[index] : colorsTop10[1]}
                />
                {index < 10 && (
                  <Item $isTop10={index < 10} $color={colorsTop10[index]} />
                )}
                <Label>
                  {firm === 'Other' ? strings.market.othersLabel : firm}
                </Label>
                <div>{`${formatNumber(market_share)}%`}</div>
              </StyledListItem>
            );
          }
          return null;
        })}
      </UnorderedList>
      {!noFilter && sortedList.length > 10 && (
        <StyledButton
          size={ButtonSize.medium}
          color={ButtonColor.transparent}
          onClick={() => setShowMore(!showMore)}>
          {!showMore ? strings.market.showMore : strings.market.showLess}
        </StyledButton>
      )}
    </>
  );
};

export default BrokersList;
